import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, API, GBP } from "@/lib/api";
import { PaymentBadge, StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Calendar, MapPin, User, Award, Download, CheckCircle2, CreditCard, FileText, ClipboardCheck, PlayCircle, Undo2, Linkedin, Share2, Clock } from "lucide-react";

const ATT_LABELS = { present: "Present", late: "Late", absent: "Absent", excused: "Excused" };
// Course content unlocks when fully paid, comped, or sponsored (client decision 2026-06-26).
const ACCESS_STATUSES = ["paid_in_full", "comped", "sponsored"];

export default function EnrolmentDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [e, setE] = useState(null);
  const [paying, setPaying] = useState("");
  const [polling, setPolling] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [accessCode, setAccessCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/my/enrolments/${id}`);
    setE(data);
    return data;
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setPolling(false); toast.error("Payment status check timed out."); return; }
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPolling(false);
          toast.success("Payment successful! Your place is confirmed.");
          await load();
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") { setPolling(false); toast.error("Payment session expired."); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, load, setParams]);

  const pay = async (option) => {
    setPaying(option);
    try {
      const { data } = await api.post("/payments/checkout", { enrolment_id: id, payment_option: option, origin_url: window.location.origin, discount_code: discount?.code || null });
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start checkout."); setPaying(""); }
  };

  const applyDiscount = async () => {    if (!discountInput.trim()) return;
    try {
      const { data } = await api.post("/discounts/validate", { code: discountInput.trim() });
      setDiscount(data);
      toast.success(`£${data.amount} code added — pay in full (over £${data.min_spend}) to use it`);
    } catch (err) {
      setDiscount(null);
      toast.error(err.response?.data?.detail || "Invalid code");
    }
  };

  const redeem = async () => {
    if (!accessCode.trim()) return;
    setRedeeming(true);
    try {
      await api.post(`/my/enrolments/${id}/redeem-code`, { code: accessCode.trim() });
      toast.success("Course unlocked — you can start learning now!");
      setAccessCode("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "That access code isn't valid.");
    }
    setRedeeming(false);
  };


  if (!e) return <div className="py-32 text-center text-[var(--ink-2)]">Loading…</div>;
  const c = e.course || {};
  const outstanding = Math.max((e.total_price || 0) - (e.amount_paid || 0), 0);
  const canPay = !["paid_in_full", "sponsored", "refunded", "comped"].includes(e.payment_status) && outstanding > 0;
  const hasAccess = ACCESS_STATUSES.includes(e.payment_status) && e.status !== "refunded";
  const reserved = !hasAccess && (e.amount_paid || 0) > 0 && e.payment_status !== "refunded" && e.status !== "rejected" && outstanding > 0;
  const refundEligible = (e.amount_paid || 0) > 0 && !e.content_accessed && e.payment_status !== "refunded" && !e.certificate_number;
  const refundRequested = e.refund_request?.status === "requested";

  const requestRefund = async () => {
    if (!window.confirm("Request a refund? Once you open any online lesson, refunds are no longer available.")) return;
    try {
      await api.post(`/my/enrolments/${id}/refund-request`, { reason: "" });
      toast.success("Refund requested — the team will review it.");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not request refund."); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10" data-testid="enrolment-detail-page">
      <Link to="/dashboard" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">← Back to my learning</Link>

      {polling && <div className="mt-4 bg-[#FFF3E0] text-[#E65100] text-sm px-4 py-3" data-testid="payment-polling">Confirming your payment…</div>}

      <div className="mt-4 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-[var(--line)] bg-white overflow-hidden">
            <img src={c.image} alt="" className="w-full aspect-[16/7] object-cover" />
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-2 mb-2"><StatusBadge status={e.status} /><PaymentBadge status={e.payment_status} /></div>
              <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">{e.course_title}</h1>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--ink-2)] mt-3">
                <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> Starts {c.start_date}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {c.location}</span>
                <span className="inline-flex items-center gap-1.5"><User size={14} /> {c.tutor_name}</span>
              </div>
              {e.cohort_date && (
                <div className="mt-3 inline-flex flex-wrap items-center gap-x-4 gap-y-1 text-sm bg-[var(--alt)] px-3 py-2" data-testid="enrolment-session">
                  <span className="font-semibold">Your session:</span>
                  <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {new Date(e.cohort_date + "T00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}</span>
                  {e.cohort_time && <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {e.cohort_time}</span>}
                  {e.cohort_location && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {e.cohort_location}</span>}
                </div>
              )}
            </div>
          </div>

          {/* Online learning */}
          {hasAccess && (
            <div className="border border-[var(--accent)] bg-white p-6" data-testid="learning-block">
              <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><PlayCircle size={18} className="text-[var(--accent)]" /> Your online course</h2>
              <p className="text-sm text-[var(--ink-2)] mb-4">Work through your lessons, resources and quizzes at your own pace. Complete everything to earn your certificate.</p>
              <Link to={`/learn/${c.id}`} data-testid="continue-learning-btn" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-6 py-3 hover:bg-[var(--accent-hover)]">
                <PlayCircle size={18} /> {e.content_accessed ? "Continue learning" : "Start learning"}
              </Link>
            </div>
          )}

          {/* Place reserved — full payment required to start */}
          {reserved && (
            <div className="border border-[var(--line)] bg-[var(--alt)] p-6" data-testid="reserved-block">
              <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><Clock size={18} className="text-[var(--accent)]" /> Your place is reserved</h2>
              <p className="text-sm text-[var(--ink-2)] mb-4">Pay the remaining balance of <span className="font-semibold text-[var(--ink)]">{GBP(outstanding)}</span> to unlock your online course and start learning.</p>
              <button onClick={() => pay("full")} disabled={paying === "full"} data-testid="reserved-pay-btn" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-6 py-3 hover:bg-[var(--accent-hover)] disabled:opacity-60">
                <CreditCard size={18} /> {paying === "full" ? "Starting checkout…" : `Pay the balance (${GBP(outstanding)})`}
              </button>
            </div>
          )}

          {/* Access code (free unlock) */}
          {!hasAccess && e.status !== "refunded" && e.status !== "rejected" && (
            <div className="border border-[var(--line)] bg-white p-6" data-testid="access-code-block">
              <h2 className="font-display font-bold text-base mb-2">Have an access code?</h2>
              <p className="text-sm text-[var(--ink-2)] mb-3">If you've been given an access code, enter it to unlock this course.</p>
              <div className="flex gap-2">
                <input value={accessCode} onChange={(ev) => setAccessCode(ev.target.value.toUpperCase())} placeholder="Enter access code" data-testid="access-code-input" className="flex-1 px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm uppercase" />
                <button onClick={redeem} disabled={redeeming || !accessCode.trim()} data-testid="access-code-redeem" className="bg-[var(--ink)] text-white font-semibold px-5 hover:bg-black disabled:opacity-60 text-sm">{redeeming ? "Checking…" : "Unlock"}</button>
              </div>
            </div>
          )}

          {/* Joining instructions */}
          <div className="border border-[var(--line)] bg-white p-6">
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><FileText size={18} /> Timetable & joining details</h2>
            {e.status === "enrolled" || e.payment_status !== "unpaid" ? (
              <div className="space-y-2">
                {c.timetable?.map((t, i) => (
                  <div key={i} className="flex justify-between border border-[var(--line)] px-4 py-3 text-sm"><span className="font-semibold">{t.label}</span><span className="text-[var(--ink-2)]">{t.date} · {t.time}</span></div>
                ))}
                <p className="text-sm text-[var(--ink-2)] mt-2">Venue / joining: {c.location}. Full joining instructions and tutor contact are sent before your start date.</p>
              </div>
            ) : (
              <p className="text-sm text-[var(--ink-2)]">Secure your place with a payment to unlock full joining instructions.</p>
            )}
          </div>

          {/* Attendance */}
          <div className="border border-[var(--line)] bg-white p-6">
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><ClipboardCheck size={18} /> Attendance</h2>
            {e.attendance?.length ? (
              <div className="space-y-2">
                {e.attendance.map((a, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-[var(--line)] pb-2" data-testid={`attendance-${i}`}>
                    <span>{a.session_label}</span>
                    <span className={`font-semibold ${a.status === "present" ? "text-[#1B5E20]" : a.status === "absent" ? "text-[#B71C1C]" : "text-[#E65100]"}`}>{ATT_LABELS[a.status]}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[var(--ink-2)]">No attendance recorded yet.</p>}
          </div>

          {/* Certificate */}
          {e.certificate_number && (
            <div className="border border-[var(--ink)] bg-[var(--ink)] text-white p-6" data-testid="certificate-block">
              <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><Award size={18} className="text-[var(--accent)]" /> Certificate achieved</h2>
              <p className="text-sm text-white/70 mb-4">Certificate no: {e.certificate_number}</p>
              <div className="flex flex-wrap gap-3">
                <a href={`${API}/certificates/${e.certificate_number}/pdf`} target="_blank" rel="noreferrer" data-testid="download-certificate" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-5 py-3 hover:bg-[var(--accent-hover)]"><Download size={16} /> Download PDF</a>
                <a href={`https://www.linkedin.com/profile/add?${new URLSearchParams({ startTask: "CERTIFICATION_NAME", name: e.course_title || "", organizationName: c.provider_name || "Get Skilled in Days Not Years", certUrl: `${window.location.origin}/verify-certificate/${e.certificate_number}`, certId: e.certificate_number }).toString()}`} target="_blank" rel="noreferrer" data-testid="enrolment-add-linkedin" className="inline-flex items-center gap-2 bg-[#0A66C2] text-white font-semibold px-5 py-3 hover:bg-[#084e96]"><Linkedin size={16} /> Add to LinkedIn</a>
                <Link to={`/verify-certificate/${e.certificate_number}`} data-testid="enrolment-share-certificate" className="inline-flex items-center gap-2 border border-white/30 px-5 py-3 hover:bg-white/10"><Share2 size={16} /> Share / verify</Link>
              </div>
            </div>
          )}
        </div>

        {/* Payment sidebar */}
        <div>
          <div className="lg:sticky lg:top-24 border border-[var(--ink)] bg-white p-6" data-testid="payment-box">
            <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><CreditCard size={18} /> Payment</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[var(--ink-2)]">Total price</span><span className="font-semibold">{GBP(e.total_price)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--ink-2)]">Amount paid</span><span className="font-semibold">{GBP(e.amount_paid)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--ink-2)]">Outstanding</span><span className="font-semibold">{GBP(outstanding)}</span></div>
              <div className="flex justify-between items-center pt-2"><span className="text-[var(--ink-2)]">Status</span><PaymentBadge status={e.payment_status} /></div>
            </div>

            {canPay ? (
              <div className="mt-5 space-y-3">
                <div data-testid="discount-section">
                  {discount ? (
                    <div className="text-sm bg-[#E8F5E9] text-[#1B5E20] px-3 py-2" data-testid="discount-applied">
                      <div className="flex items-center justify-between">
                        <span>Code {discount.code}: −{GBP(discount.amount)}</span>
                        <button onClick={() => { setDiscount(null); setDiscountInput(""); }} className="text-xs underline">remove</button>
                      </div>
                      <div className="text-[11px] text-[#1B5E20]/80 mt-0.5">Pay in full to use this code (spend over {GBP(discount.min_spend)})</div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input data-testid="discount-input" value={discountInput} onChange={(e) => setDiscountInput(e.target.value)} placeholder="Discount code" className="flex-1 px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm uppercase" />
                      <button onClick={applyDiscount} data-testid="discount-apply" className="px-4 py-2 border border-[var(--ink)] text-sm font-semibold hover:bg-[var(--ink)] hover:text-white">Apply</button>
                    </div>
                  )}
                </div>
                {e.payment_status === "unpaid" && e.deposit_amount > 0 && (
                  <button onClick={() => pay("deposit")} disabled={!!paying} data-testid="pay-deposit-button" className="w-full bg-[var(--accent)] text-white font-semibold py-3.5 hover:bg-[var(--accent-hover)] disabled:opacity-60">
                    {paying === "deposit" ? "Redirecting…" : `Pay deposit · ${GBP(e.deposit_amount)}`}
                  </button>
                )}
                <button onClick={() => pay("full")} disabled={!!paying} data-testid="pay-full-button" className="w-full border border-[var(--ink)] font-semibold py-3.5 hover:bg-[var(--ink)] hover:text-white transition-colors disabled:opacity-60">
                  {paying === "full" ? "Redirecting…" : `Pay in full · ${GBP(outstanding - (discount && outstanding > discount.min_spend ? discount.amount : 0))}`}
                </button>
                {discount && (
                  <p className="text-[11px] text-center text-[var(--ink-2)]" data-testid="discount-fullpay-note">
                    {outstanding > discount.min_spend
                      ? `£${discount.amount} off applied when you pay in full.`
                      : `This code needs a full payment over £${discount.min_spend}.`}
                  </p>
                )}
                <p className="text-xs text-[var(--ink-2)] text-center">Test card: 4242 4242 4242 4242</p>
              </div>
            ) : (
              <div className="mt-5 flex items-center gap-2 text-sm text-[#1B5E20] bg-[#E8F5E9] px-4 py-3" data-testid="payment-complete"><CheckCircle2 size={16} /> No payment outstanding.</div>
            )}
          </div>

          {/* Refunds */}
          {e.payment_status === "refunded" ? (
            <div className="mt-4 border border-[var(--line)] bg-white p-5 text-sm text-[var(--ink-2)]" data-testid="refund-done">This enrolment has been refunded.</div>
          ) : (e.amount_paid || 0) > 0 && (
            <div className="mt-4 border border-[var(--line)] bg-white p-5" data-testid="refund-box">
              <h3 className="font-display font-bold text-sm mb-2 flex items-center gap-2"><Undo2 size={15} /> Refunds</h3>
              {refundRequested ? (
                <p className="text-sm text-[#E65100]" data-testid="refund-requested">Refund requested — the team is reviewing it.</p>
              ) : refundEligible ? (
                <>
                  <p className="text-xs text-[var(--ink-2)] mb-3">You can request a refund until you open your first online lesson.</p>
                  <button onClick={requestRefund} data-testid="request-refund-btn" className="w-full border border-[var(--ink)] text-sm font-semibold py-2.5 hover:bg-[var(--ink)] hover:text-white transition-colors">Request a refund</button>
                </>
              ) : (
                <p className="text-xs text-[var(--ink-2)]" data-testid="refund-locked">{e.certificate_number ? "Refunds aren't available after completion." : "Refunds aren't available once you've accessed the online course content."}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
